import type { FightSettlement, FightTurnView, FightView } from '@wyn/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from '@tanstack/react-router';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { ReportButton } from '../components/Report.tsx';
import { PageTitle } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { UI } from '../icons/ui-icons.ts';
import { fightCurrentQuery, useFight } from '../lib/queries.ts';
import { type Tone, TONE_BORDER } from '../lib/theme.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const SLOT_ICON = { mainHand: UI.mainHand, offHand: UI.offHand, ranged: UI.ranged, fists: UI.fists } as const;

const CHOICES = [
  { id: 'spare', label: 'Spare', blurb: 'Leave them be. More Renown.' },
  { id: 'rob', label: 'Rob', blurb: 'Take 5–15% of their purse.' },
  { id: 'maim', label: 'Maim', blurb: 'A long stay in the Infirmary.' },
] as const;

/** The attacker's view of a fight in progress, and what came of the last one. */
export function Fight() {
  const me = useMe();
  const current = useQuery(fightCurrentQuery());
  const f = useFight();
  if (me.isPending || current.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  const fight = current.data?.fight ?? null;
  const settlement = f.finish.data?.settlement ?? f.withdraw.data?.settlement ?? f.turn.data?.settlement ?? null;
  const lastFight = f.turn.data?.fight ?? null;
  const busy = f.turn.isPending || f.finish.isPending || f.withdraw.isPending;
  const error = f.turn.error ?? f.finish.error ?? f.withdraw.error ?? current.error;

  return (
    <GameShell me={me.data} skew={me.skew}>
      <div className="space-y-6">
        {fight ? (
          <>
            <Combatants fight={fight} />
            {fight.status === 'fighting' && (
              <section className="panel space-y-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="heading">Strike with</h2>
                  <span className="font-mono text-sm text-ink-faint">Turn {fight.turn + 1} of {fight.maxTurns}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {fight.weapons.map((w) => (
                    <button
                      key={w.slot}
                      type="button"
                      className="btn"
                      disabled={busy || !w.usable}
                      onClick={() => f.turn.mutate({ fightId: fight.id, slot: w.slot })}
                    >
                      <Icon name={SLOT_ICON[w.slot]} className="mr-1" />
                      {w.name}
                      {w.ammo !== null && <span className="ml-1 font-mono text-xs text-ink-faint">({w.ammo})</span>}
                    </button>
                  ))}
                </div>
                <button type="button" className="btn-quiet" disabled={busy} onClick={() => f.withdraw.mutate()}>
                  Break off
                </button>
              </section>
            )}
            {fight.status === 'won' && (
              <section className="panel space-y-3 border-verdigris/60">
                <h2 className="heading text-verdigris">{fight.defender.name} is down</h2>
                <p className="text-ink-dim">What will you do with them?</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {CHOICES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="border border-rule px-3 py-2 text-left hover:border-gilt disabled:opacity-40"
                      disabled={busy}
                      onClick={() => f.finish.mutate({ fightId: fight.id, choice: c.id })}
                    >
                      <div>{c.label}</div>
                      <div className="text-xs text-ink-faint">{c.blurb}</div>
                    </button>
                  ))}
                </div>
              </section>
            )}
            <FightLog turns={fight.log} attacker={fight.attacker.name} defender={fight.defender.name} />
          </>
        ) : settlement ? (
          <>
            {lastFight && <Combatants fight={lastFight} />}
            <Outcome s={settlement} />
            {lastFight && <FightLog turns={lastFight.log} attacker={lastFight.attacker.name} defender={lastFight.defender.name} />}
          </>
        ) : (
          <section className="panel space-y-2">
            <PageTitle icon={UI.fight}>No fight</PageTitle>
            <p className="text-ink-dim">Pick a quarrel with someone under Folk about town.</p>
            <Link to="/town" className="btn inline-block">Back to town</Link>
          </section>
        )}
        <ErrorNotice error={error} />
      </div>
    </GameShell>
  );
}

function Combatants({ fight }: { fight: FightView }) {
  return (
    <section className="panel grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
      <Side side={fight.attacker} label="You" />
      <div className="text-center text-2xl text-gilt"><Icon name={UI.fight} /></div>
      <Side side={fight.defender} label={`Level ${fight.defender.level}`} report />
    </section>
  );
}

function Side({ side, label, report = false }: { side: FightView['attacker']; label: string; report?: boolean }) {
  const pct = Math.max(0, Math.min(100, (side.life / Math.max(1, side.maxLife)) * 100));
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-lg">
          {side.name}
          {report && <ReportButton target={{ kind: 'player', name: side.name }} what={side.name} className="ml-2 text-sm" />}
        </span>
        <span className="text-xs text-ink-faint">{label}</span>
      </div>
      <div className="h-2 w-full bg-rule" role="meter" aria-label={`${side.name} Life`} aria-valuenow={side.life} aria-valuemin={0} aria-valuemax={side.maxLife}>
        <div className="h-full bg-blood transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-right font-mono text-xs tabular-nums text-ink-dim"><Icon name={UI.life} className="mr-1 text-blood" />{side.life}/{side.maxLife}</div>
    </div>
  );
}

const OUTCOME_TONE: Record<FightSettlement['result'], Tone> = {
  won: 'good',
  lost: 'bad',
  stalemate: 'muted',
  withdrew: 'muted',
};

function Outcome({ s }: { s: FightSettlement }) {
  return (
    <section className={`panel space-y-2 border-l-4 ${TONE_BORDER[OUTCOME_TONE[s.result]]}`} role="status">
      <h1 className="text-2xl">{s.result === 'won' ? 'Victory' : s.result === 'lost' ? 'Beaten' : s.result === 'stalemate' ? 'Stalemate' : 'Broken off'}</h1>
      <p>{s.text}</p>
      <p className="font-mono text-sm text-ink-dim">
        {s.turns} turns · dealt {s.damageDealt} · took {s.damageTaken}
        {s.prestige > 0 && ` · +${s.prestige} Prestige`}
      </p>
      <Link to="/town" className="btn inline-block">Back to town</Link>
    </section>
  );
}

/** The turn-by-turn report. Also used by the Chronicle. */
export function FightLog({ turns, attacker, defender }: { turns: FightTurnView[]; attacker: string; defender: string }) {
  if (turns.length === 0) return null;
  return (
    <section className="panel space-y-1">
      <h2 className="heading">Blow by blow</h2>
      <ol className="space-y-1 text-sm">
        {[...turns].reverse().map((t) => (
          <li key={t.turn} className="grid grid-cols-[2.5rem_1fr] gap-2 border-b border-rule/60 pb-1 last:border-0">
            <span className="font-mono text-xs leading-5 text-ink-faint">{t.turn}</span>
            <span className="space-y-0.5">
              {t.strikes.map((x, i) => (
                <span key={i} className="block">
                  <span className={x.by === 'attacker' ? 'text-ink' : 'text-ink-dim'}>{x.by === 'attacker' ? attacker : defender}</span>{' '}
                  {x.slot === 'fists' ? 'throws a punch' : x.slot === 'ranged' ? `looses with the ${x.weapon}` : `swings the ${x.weapon}`}:{' '}
                  {x.hit ? <span className="text-blood">{x.damage} damage</span> : <span className="text-ink-faint">misses</span>}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
